import styled from 'styled-components';
import { Tab, Textarea, TimelineMarker } from 'react-rainbow-components';
import { ChatBubble } from 'components/CustomerActivity/styled';
import {
    BACKGROUND,
    DIVIDER,
    GREY_50,
    GREY_100,
    GREY_300,
    GREY_400,
    GREY_500,
    GREY_600,
    GREY_800,
    GREEN_100,
    GREEN_600,
    WHITE,
} from '../../../constants';

export const DrawerBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: 100%;
`;

export const TabsetContainer = styled.div`
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 1;
    margin: -0.75rem 0 1rem;
    background: ${WHITE};
`;

export const StyledTab = styled(Tab)`
    display: flex;
    flex: 1 0 auto;
    width: auto;
    min-width: max-content;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    color: ${GREY_800};

    &&,
    && button {
        height: 2.75rem;
    }

    button::before {
        border-radius: 0;
        height: 2px;
    }
`;

export const TabLabel = styled.span`
    display: flex;
    padding: 0 0.75rem;
    white-space: nowrap;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.125rem;
    letter-spacing: 0.02688rem;

    svg {
        width: 1.25rem;
        height: 1.25rem;
    }
`;

export const DocumentActionGroup = styled.div`
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.25rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.1);
`;

export const PreviewPane = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    border-radius: 0.75rem;

    > * {
        flex: 1;
        min-height: 0;
        width: 100%;
    }

    > ${DocumentActionGroup} {
        flex: 0 0 auto;
        width: auto;
        min-height: 0;
    }
`;

export const DetailsPane = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: auto;
    gap: 1.5rem;
    padding: 0.5rem 0.5rem 1.5rem;
`;

export const NotesPane = styled(DetailsPane)`
    gap: 0.75rem;
`;

export const SignaturePane = styled(DetailsPane)``;

export const NoteCard = styled.div`
    padding: 0.75rem 1rem;
    border: 1px solid ${DIVIDER};
    border-radius: 0.5rem;
    background: ${WHITE};
`;

export const NoteLabel = styled.div`
    margin-bottom: 0.25rem;
    color: ${GREY_600};
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

export const NoteHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.25rem;

    ${NoteLabel} {
        margin-bottom: 0;
    }
`;

export const NoteEditButton = styled.button`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${GREY_400};
    cursor: pointer;

    &:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.04);
        color: ${GREY_800};
    }

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

export const NoteEditActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding-top: 0.5rem;
`;

export const InternalNoteInput = styled(Textarea)`
    textarea {
        min-height: 6.5rem;
        border-radius: 0.375rem;
    }
`;

export const NoteText = styled.p`
    margin: 0;
    color: ${GREY_500};
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.4;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

export const MetadataContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1.5rem;
    padding: 0.625rem 1rem;
    border-radius: 0.5rem;
    background: ${BACKGROUND};
`;

export const MetadataItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.25rem;
    color: ${GREY_400};

    > svg {
        width: 1rem;
        height: 1rem;
        color: ${GREY_300};
    }
`;

export const DetailSection = styled.section`
    display: flex;
    flex-direction: column;
    align-self: stretch;
`;

export const SectionTitle = styled.div`
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: ${GREY_300};
    text-transform: uppercase;
`;

export const RecordsList = styled.div<{ $columns?: number }>`
    display: grid;
    grid-template-columns: repeat(${({ $columns }) => $columns || 1}, minmax(0, 1fr));
    column-gap: 1rem;
    align-self: stretch;
`;

export const RecordContainer = styled.div`
    display: flex;
    min-width: 0;
    color: ${GREY_500};
`;

export const RecordIcon = styled.div`
    padding: 0.75rem;
    color: ${GREY_400};

    > svg {
        width: 1.5rem;
        height: 1.5rem;
    }
`;

export const RecordContent = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0.81rem 0 0.5rem;
`;

export const RecordLabel = styled.div`
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1.375rem;
    color: ${GREY_600};
`;

export const RecordValue = styled.div`
    color: ${GREY_400};
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5rem;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
`;

export const EmptyLabel = styled.span`
    font-style: italic;
    color: ${GREY_300};
`;

export const SummaryList = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${DIVIDER};
    border-radius: 0.75rem;
    background: ${WHITE};
`;

export const SummaryRow = styled.div<{ $total?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.625rem 1.25rem;
    font-weight: ${({ $total }) => ($total ? 700 : 400)};

    & + & {
        border-top: 1px solid ${DIVIDER};
    }
`;

export const SummaryLabel = styled.span`
    color: ${GREY_600};
    font-size: 0.875rem;
`;

export const SummaryAmount = styled.span`
    color: ${GREY_500};
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const ItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

export const ItemCard = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem 1.25rem;
    border: 1px solid ${DIVIDER};
    border-radius: 0.75rem;
    background: ${WHITE};
`;

export const ItemMainRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
`;

export const ItemDescription = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    line-height: 1.375rem;
    color: ${GREY_800};
    overflow-wrap: anywhere;
    white-space: pre-wrap;
`;

export const ItemPricing = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    flex-shrink: 0;
`;

export const ItemPricingRecord = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
`;

export const ItemAmount = styled.div`
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.5rem;
    color: ${GREY_500};
`;

export const CustomerTileContainer = styled.div`
    display: flex;
    padding: 0.5rem 0.75rem;
`;

export const ItemFields = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1rem;
    margin-top: 0.5rem;
    padding-top: 0.25rem;
    border-top: 1px solid ${DIVIDER};
`;

export const HistoryPane = styled(DetailsPane)`
    gap: 0;
    padding-top: 1rem;
`;

export const HistoryMarker = styled(TimelineMarker)`
    min-width: 0;

    > div:last-child {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
    }

    h1 {
        margin: 0;
        min-width: 0;
        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;
        color: inherit;
    }

    > div:last-child > div:first-child > p:empty {
        display: none;
    }
`;

export const HistoryTitle = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.5rem;
    min-width: 0;
`;

export const HistoryTime = styled.span`
    color: ${GREY_400};
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.5rem;
    white-space: nowrap;
`;

export const HistoryBubble = styled(ChatBubble)`
    width: 100%;
    margin-right: 0;
    background: ${GREY_100};
    color: ${GREY_600};

    &::after {
        display: none;
    }
`;

export const HistoryEditNote = styled.p`
    margin: 0 0 0.75rem;
    color: ${GREY_800};
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
`;

export const HistoryChangeCard = styled.div`
    display: flex;
    align-items: stretch;
    width: 100%;
    overflow: hidden;
    border: 1px solid ${DIVIDER};
    border-radius: 0.75rem;
    background: ${WHITE};
`;

export const HistoryChangeMark = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 3rem;
    border-right: 1px solid ${DIVIDER};
    color: ${WHITE};

    span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;
        border-radius: 50%;
        background: ${GREEN_600};
    }

    svg {
        width: 0.9rem;
        height: 0.9rem;
    }
`;

export const HistoryChangeRows = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
`;

export const HistoryChangeRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
    padding: 0.75rem 1rem;

    & + & {
        border-top: 1px solid ${DIVIDER};
    }
`;

export const HistoryChangeLabel = styled.span`
    min-width: 0;
    color: ${GREY_800};
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
`;

export const HistoryChangeValues = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.5rem;
    color: ${GREY_500};
    font-size: 0.875rem;
`;

export const HistoryValuePill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 999px;
    background: ${GREY_50};
    color: ${GREY_800};
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.25rem;
`;

export const HistoryValueNext = styled(HistoryValuePill)`
    background: ${GREEN_100};
`;

export const HistoryReason = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.75rem;
    color: ${GREY_500};
    font-size: 0.875rem;
    line-height: 1.25rem;

    svg {
        width: 1rem;
        height: 1rem;
    }

    strong {
        color: ${GREY_800};
        font-weight: 700;
    }
`;

export const HistoryLoadMore = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 1rem;
`;

export const ItemField = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0.5rem 0 0.25rem;
`;
